import {
  Page,
  Spinner,
  Divider
} from "@whitespace/whitespace-ui";
import { useEffect, useState, useCallback } from "react";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { Link, useNavigate } from "react-router-dom";
import IGetSlaOverviewResponseModel from "interfaces/Response/IGetSlaOverviewResponseModel";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import {
  Breadcrumb,
  Button, Card, Col, Container, Row
} from "react-bootstrap";
import { format } from "date-fns";
import ISlaOverviewViewModel from "interfaces/ISlaOverviewViewModel";
import { formatDecimalToHoursAndMinutes } from "helpers/DateUtils";
import { mapSlaOverviewToViewModel } from "./DataMappers/ManageSlaOverviewMapper";

import "./ManageSla.scss";

const ManageSla = () => {
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const {
    isSending, errors, data, sendRequest
  } = useAmplifyREST();
  const navigate = useNavigate();
  const [slaOverviewViewModel, setSlaOverviewViewModel] = useState<ISlaOverviewViewModel | undefined>();

  useEffect(() => {
    sendRequest("settings/sla/overview", "GET");
  }, [sendRequest]);

  useEffect(() => {
    if (data) {
      const response = data as IGetSlaOverviewResponseModel;
      if (response.slaOverview) {
        setSlaOverviewViewModel(mapSlaOverviewToViewModel(response.slaOverview));
      }
    }
  }, [data, setSlaOverviewViewModel]);

  useEffect(() => {
    if (errors) setShowExceptions(true);
  }, [errors]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  return (
    <Page>
      {showExceptions && errors && (
        <AmplifyError
          errors={errors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="manage-sla">
              SLA Management
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Card.Body className="no-padding">
          <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
            <Col xs={6} className="d-none d-md-block">
              <Breadcrumb className="left-margin-large no-wrap">
                <Breadcrumb.Item
                  data-testid="breadcrumb-settings"
                  linkAs={Link}
                  linkProps={{ to: RoutePathsEnum.Settings }}
                  onClick={() => navigate(RoutePathsEnum.Settings)}
                >
                  Settings
                </Breadcrumb.Item>
                <Breadcrumb.Item
                  active
                  data-testid="breadcrumb-update-sla-management"
                >
                  SLA Management
                </Breadcrumb.Item>
              </Breadcrumb>
            </Col>
            <Col className="text-end">
              <Button
                variant="primary"
                className="right-margin-small"
                onClick={() => navigate(RoutePathsEnum.SettingsSlaUpdate)}
                data-testid="btnEditSla"
                aria-label="Edit SLA"
              >
                Edit SLA
              </Button>
              <Button
                variant="primary"
                className="right-margin-small"
                onClick={() => navigate(RoutePathsEnum.SettingsSlaDwhUpdate)}
                data-testid="btnEditDwh"
                aria-label="Edit DWH (Driver Working Hours)"
              >
                Edit DWH
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate(RoutePathsEnum.Settings)}
                data-testid="btnCancel"
                aria-label="Cancel and back to Settings"
              >
                Cancel
              </Button>
            </Col>
          </Row>
          <Divider />
          {
            slaOverviewViewModel && (
              <section className="pt-2 section-sla" role="complementary" data-testid="formElements">
                <Container className="ms-3 mt-1">
                  <Row className="d-flex mb-2">
                    <Col><h2>Collection SLA</h2></Col>
                  </Row>
                  <Row className="d-flex">
                    <Col className="horiz-form-label form-label">
                      SLA Default Time:
                    </Col>
                  </Row>
                  <Row className="d-flex">
                    <Col data-testid="slaDefault" className="horiz-form-label form-label">
                      {formatDecimalToHoursAndMinutes(slaOverviewViewModel.slaDefault!)}
                    </Col>
                  </Row>
                  <Row className="d-flex mb-2 mt-2">
                    <Col><h2>Driver Working Hours</h2></Col>
                  </Row>
                  <Row className="d-flex">
                    <Col className="horiz-form-label form-label">Start Time:</Col>
                  </Row>
                  <Row className="d-flex">
                    <Col id="dwhStartTimeLabel" data-testid="dwhStartTime" className="horiz-form-label form-label">
                      {slaOverviewViewModel.dwhStartTime && format(slaOverviewViewModel.dwhStartTime, "HH:mm")}
                    </Col>
                  </Row>
                  <Row className="d-flex">
                    <Col className="horiz-form-label form-label">End Time:</Col>
                  </Row>
                  <Row className="d-flex">
                    <Col id="dwhEndTimeLabel" data-testid="dwhEndTime" className="horiz-form-label form-label">
                      {slaOverviewViewModel.dwhEndTime && format(slaOverviewViewModel.dwhEndTime, "HH:mm")}
                    </Col>
                  </Row>
                </Container>
              </section>
            )
          }

        </Card.Body>
      </Card>
    </Page>
  );
};
export default ManageSla;
